import React from 'react';
import { Building2, HeartHandshake, Network, WandSparkles, SearchCheck, ChartPie, FilePen, Video, Check } from 'lucide-react';
import MarketingPage, { ContentCard } from '@/components/marketing/MarketingPage';
import { pageJsonLd, faqJsonLd } from '@/components/shared/Seo';

const CAPABILITIES = [
  { id: 'ai-matching', icon: WandSparkles, color: '#6D5EF7', title: 'AI-powered matching', desc: 'Not a job board — a vector matching engine that scores fit with real percentages, not keyword hits.' },
  { id: 'screening', icon: SearchCheck, color: '#12B6A6', title: 'Intelligent screening', desc: 'Auto-generated, role-calibrated questions. Jay conducts, transcribes and scores every candidate.' },
  { id: 'reporting', icon: ChartPie, color: '#8B5CF6', title: 'Reporting & analytics', desc: 'Exportable pipeline reports, DEI signals, funnel diagnostics.' },
  { id: 'offers', icon: FilePen, color: '#10B981', title: 'Offer automation', desc: 'Generate, route, and track offer letters the moment a candidate is selected.' },
];

const FAQ = [
  { q: 'How is Numuv priced for hiring teams?',
    a: 'Usage-based, not per seat. Start free, then buy Mu Points credits and spend them on AI actions such as matching a candidate, running an AI screening, generating a report or renewing a job. Enterprise plans are custom.' },
  { q: 'Is Numuv a job board?',
    a: 'No. Numuv is a matching, screening and pipeline platform. You post a role, Numuv retrieves and ranks candidates by vector similarity, Jay screens them by voice or video, and every candidate moves through a seven-stage journey with full audit history.' },
  { q: 'Does Numuv reject candidates automatically?',
    a: 'Only if you choose to. Each job can define an optional AI job-fit threshold that auto-selects or auto-rejects after screening. It is off by default and never overrides a decision a human has already made.' },
  { q: 'Can staffing organisations manage their own candidate pool?',
    a: 'Yes. Staffing organisations bulk-upload resumes, keep candidate rate cards private, run a team hierarchy with granular permissions, and are billed per screening and per offer in Mu Points.' },
];

const JSONLD = pageJsonLd({
  name: 'Numuv for hiring and staffing teams',
  description: 'AI matching, autonomous AI interviews, screening and offer automation for in-house hiring teams and staffing organisations, with full team-hierarchy support.',
  path: '/for-employers',
  breadcrumbs: [{ name: 'For employers', path: '/for-employers' }],
  extra: [faqJsonLd(FAQ)],
});

export default function ForEmployers() {
  return (
    <MarketingPage
      jsonLd={JSONLD}
      faq={FAQ}
      seoTitle="For Hiring & Staffing Teams — AI recruitment that scales"
      seoDescription="Numuv unifies staffing organisations and hiring teams on one platform: AI matching, autonomous interviews, screening, and offer automation with full team-hierarchy support."
      seoPath="/for-employers"
      seoKeywords="AI recruiting platform, staffing agency software, hiring platform, applicant tracking, AI screening, offer management"
      eyebrow="For Employers"
      title="Hire faster. Screen smarter. Scale without headcount."
      subtitle="Whether you're a hiring team filling roles or a staffing organisation placing candidates, Numuv gives you the AI muscle to move faster without losing quality."
    >
      <div id="staffing" className="grid md:grid-cols-2 gap-5 md:gap-7 mt-4">
        <ContentCard>
          <span className="grid h-12 w-12 place-items-center rounded-[14px] bg-[#8B5CF6]/12 text-[#8B5CF6]"><HeartHandshake className="w-5 h-5" /></span>
          <h3 className="dis mt-5 text-[20px] font-semibold t1" style={{ fontFamily: "'Sora', sans-serif" }}>Staffing Organizations</h3>
          <p className="mt-2 text-[14px] leading-[22px] t3">Onboard, nurture, and place candidates while managing every partnership from a single command center.</p>
          <ul className="mt-5 space-y-2.5 text-[13.5px] t2">
            {['Candidate onboarding pipelines', 'Partnership management + shared talent pools', 'Connection building at scale', 'Team hierarchy with sub-recruiters'].map((b) => (
              <li key={b} className="flex items-center gap-2.5"><Check className="w-4 h-4 text-[#8B5CF6] shrink-0" />{b}</li>
            ))}
          </ul>
        </ContentCard>
        <ContentCard id="hiring">
          <span className="grid h-12 w-12 place-items-center rounded-[14px] bg-[#4F7BF7]/12 text-[#4F7BF7]"><Building2 className="w-5 h-5" /></span>
          <h3 className="dis mt-5 text-[20px] font-semibold t1" style={{ fontFamily: "'Sora', sans-serif" }}>Hiring Organizations</h3>
          <p className="mt-2 text-[14px] leading-[22px] t3">Post roles, define AI screening questions, and track every candidate across your whole team hierarchy.</p>
          <ul className="mt-5 space-y-2.5 text-[13.5px] t2">
            {['Job posting & AI question setup', 'Live candidate tracking + Kanban', 'Team hierarchy with fine-grained permissions', 'Intelligent screening reports and offer automation'].map((b) => (
              <li key={b} className="flex items-center gap-2.5"><Check className="w-4 h-4 text-[#4F7BF7] shrink-0" />{b}</li>
            ))}
          </ul>
        </ContentCard>
      </div>

      <div className="mt-14 md:mt-20">
        <div className="text-center mb-10 md:mb-14">
          <span className="text-[11.5px] md:text-[12px] font-semibold uppercase tracking-[0.18em] text-[#6D5EF7]">Capabilities</span>
          <h2 className="dis mt-3 md:mt-4 text-[28px] md:text-[38px] font-bold tracking-[-0.03em] t1" style={{ fontFamily: "'Sora', sans-serif" }}>Everything you need, in one platform</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {CAPABILITIES.map((c) => (
            <ContentCard id={c.id} key={c.id} className="!p-6">
              <span className="grid h-11 w-11 place-items-center rounded-[13px]" style={{ background: `${c.color}1F`, color: c.color }}><c.icon className="w-5 h-5" /></span>
              <h3 className="dis mt-4 text-[16.5px] font-semibold t1" style={{ fontFamily: "'Sora', sans-serif" }}>{c.title}</h3>
              <p className="mt-2 text-[13.5px] leading-[21px] t3">{c.desc}</p>
            </ContentCard>
          ))}
        </div>
      </div>

      <div id="hierarchy" />
      <ContentCard className="mt-10">
        <div className="flex items-start gap-4">
          <span className="grid h-12 w-12 place-items-center rounded-[14px] bg-[#6D5EF7]/12 text-[#6D5EF7] shrink-0"><Network className="w-5 h-5" /></span>
          <div>
            <h3 className="dis text-[20px] font-semibold t1" style={{ fontFamily: "'Sora', sans-serif" }}>Team hierarchy, done right</h3>
            <p className="mt-2 text-[14px] leading-[22px] t2 max-w-[720px]">
              Model your whole org — recruiters, sub-recruiters, heads, admins — with fine-grained visibility and approval chains that match how your team actually works.
            </p>
          </div>
        </div>
      </ContentCard>

      <ContentCard id="recordings" className="mt-6">
        <div className="flex items-start gap-4">
          <span className="grid h-12 w-12 place-items-center rounded-[14px] bg-[#F59E0B]/14 text-[#F59E0B] shrink-0"><Video className="w-5 h-5" /></span>
          <div>
            <h3 className="dis text-[20px] font-semibold t1" style={{ fontFamily: "'Sora', sans-serif" }}>Recorded, searchable interviews</h3>
            <p className="mt-2 text-[14px] leading-[22px] t2 max-w-[720px]">
              Every Jay interview is captured, transcribed, and stored. Search across every session, jump to the exact answer, and share highlights with hiring managers.
            </p>
          </div>
        </div>
      </ContentCard>
    </MarketingPage>
  );
}
